/**
 * Zod schema + id vocabulary for **profile customization** (APP_SPEC §13.2):
 * a selected profile *title* plus three *independently* selectable cosmetic
 * slots — an avatar frame, a hero background, and a side decoration.
 *
 * This is deliberately the same shape of contract as `avatar.ts`:
 * - Every field is `.nullable().catch(null).default(null)`, so a partial or
 *   stale object still parses to a complete config and an id that is later
 *   renamed or removed degrades to "nothing selected" rather than throwing.
 * - `null` means *no selection*: the title row is hidden, and a cosmetic slot
 *   falls back to its neutral placeholder asset.
 *
 * MODULARITY (APP_SPEC §13.2): the three cosmetic slots are never intrinsically
 * linked. A "theme preset" is a web-only convenience that writes all three slot
 * ids at once; the persisted customization is always the three resolved ids,
 * never a `theme = "x"`. There is no preset field here on purpose.
 *
 * The id lists below are the canonical vocabulary. The web app owns the
 * display catalog (label, artwork path, `unlocked` flag, sort order) keyed by
 * these ids — see `apps/web/src/features/profile/profile-assets.ts`.
 */
import { z } from 'zod';

/** Selectable profile titles (APP_SPEC §13.2). Not every title is available to every user. */
export const PROFILE_TITLE_IDS = [
  'first-light',
  'dungeon-delver',
  'word-smith',
  'riddle-breaker',
  'puzzle-warden',
  'lorekeeper',
  'archivist',
  'nightscholar',
] as const;

/** Selectable avatar-frame cosmetics (APP_SPEC §13.2). */
export const AVATAR_FRAME_IDS = [
  'ancient-gold',
  'forgotten-library',
  'emerald-thorn',
  'candlelit-brass',
  'runed-oak',
] as const;

/** Selectable profile-hero background cosmetics (APP_SPEC §13.2). */
export const HERO_BACKGROUND_IDS = [
  'moonlit-library',
  'forest-ruin',
  'arcane-study',
  'dawn-keep',
  'deep-archive',
] as const;

/** Selectable profile side-decoration cosmetics (APP_SPEC §13.2). */
export const SIDE_DECORATION_IDS = [
  'dungeon-books',
  'hanging-banner',
  'candles-and-tomes',
  'ivy-pillar',
] as const;

export type ProfileTitleId = (typeof PROFILE_TITLE_IDS)[number];
export type AvatarFrameId = (typeof AVATAR_FRAME_IDS)[number];
export type HeroBackgroundId = (typeof HERO_BACKGROUND_IDS)[number];
export type SideDecorationId = (typeof SIDE_DECORATION_IDS)[number];

/** A nullable, self-healing enum field: unknown/renamed/removed id -> `null`. */
function nullableCosmetic<const T extends readonly [string, ...string[]]>(ids: T) {
  return z.enum(ids).nullable().catch(null).default(null);
}

/**
 * A full, valid profile-customization configuration. Every field defaults to
 * `null` (nothing selected) and never throws on a bad stored value.
 */
export const profileCustomizationSchema = z.object({
  selectedTitleId: nullableCosmetic(PROFILE_TITLE_IDS),
  avatarFrameId: nullableCosmetic(AVATAR_FRAME_IDS),
  heroBackgroundId: nullableCosmetic(HERO_BACKGROUND_IDS),
  sideDecorationId: nullableCosmetic(SIDE_DECORATION_IDS),
});

export type ProfileCustomization = z.infer<typeof profileCustomizationSchema>;

/** The customization a user has before they choose anything. */
export const DEFAULT_PROFILE_CUSTOMIZATION: ProfileCustomization = profileCustomizationSchema.parse(
  {},
);

/**
 * Normalizes an untrusted/stored value into a full `ProfileCustomization`.
 * Never throws — used by the public-profile projection and the `/api/auth/me`
 * response. A `null`/`undefined` input (a user who never customized) yields
 * the all-`null` default rather than `null`, so callers always get an object.
 */
export function coerceProfileCustomization(value: unknown): ProfileCustomization {
  if (value === null || value === undefined) return { ...DEFAULT_PROFILE_CUSTOMIZATION };
  const parsed = profileCustomizationSchema.safeParse(value);
  return parsed.success ? parsed.data : { ...DEFAULT_PROFILE_CUSTOMIZATION };
}

/**
 * Builds a `ProfileCustomization` from four separately-stored nullable id
 * columns (`users.profile_title_id` etc.). Each id is still run through the
 * schema so a value that left the vocabulary reads back as `null`.
 */
export function profileCustomizationFromColumns(columns: {
  selectedTitleId: string | null;
  avatarFrameId: string | null;
  heroBackgroundId: string | null;
  sideDecorationId: string | null;
}): ProfileCustomization {
  return coerceProfileCustomization(columns);
}
